import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Alert,
  CircularProgress,
  Box,
  Stepper,
  Step,
  StepLabel,
  Fade,
  Slide
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { purchaseCredits, fetchBalance } from '../../redux/slices/creditSlice';
import { fetchActivePackages } from '../../redux/slices/creditPackageSlice';
import { getSettingValue } from '../../redux/slices/appSettingSlice';
import PaymentForm from './PaymentForm';
import alertify from 'alertifyjs';

/**
 * CreditPurchaseModal Component
 * Modal for purchasing credits (used in DocumentWizardPage)
 * Phase 7: Credit System
 * 
 * Props:
 * - open: boolean - Modal açık mı?
 * - onClose: function - Modal kapatma callback
 * - onSuccess: function - Ödeme başarılı olunca çağrılır (bakiye güncellenince)
 * - requiredCredits: number (opsiyonel) - Gerekli kredi miktarı (document wizard için)
 * - preselectedPackage: object (opsiyonel) - Önceden seçilmiş paket (CreditPurchasePage için)
 */
const CreditPurchaseModal = ({ open, onClose, onSuccess, requiredCredits = null, preselectedPackage = null }) => {
  const dispatch = useDispatch();
  const { balance, loading: creditLoading } = useSelector(state => state.credits);
  const { activePackages, loading: packageLoading } = useSelector(state => state.creditPackages);

  const [currentStep, setCurrentStep] = useState(1); // 1: Package Selection, 2: Payment
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentGatewayEnabled, setPaymentGatewayEnabled] = useState(false);
  const [supportEmail, setSupportEmail] = useState('support@contractcode.com');
  const [supportPhone, setSupportPhone] = useState('+90 (XXX) XXX XX XX');

  useEffect(() => {
    if (open) {
      // Fetch balance and packages
      dispatch(fetchBalance());
      dispatch(fetchActivePackages());
      
      // Fetch payment gateway and support settings
      // Always fetch fresh data from API (don't rely on cache for critical settings)
      const loadSettings = async () => {
        try {
          // Payment gateway enabled - Always fetch fresh
          const paymentGatewayKey = 'payment_gateway_enabled';
          try {
            const result = await dispatch(getSettingValue(paymentGatewayKey)).unwrap();
            const value = result?.value || null;
            setPaymentGatewayEnabled(value === 'true');
          } catch (error) {
            console.error('Error loading payment_gateway_enabled:', error);
            setPaymentGatewayEnabled(false); // Default fallback
          }

          // Support email - Always fetch fresh
          const supportEmailKey = 'support_email';
          try {
            const result = await dispatch(getSettingValue(supportEmailKey)).unwrap();
            const value = result?.value || null;
            setSupportEmail(value || 'support@contractcode.com');
          } catch (error) {
            console.error('Error loading support_email:', error);
            setSupportEmail('support@contractcode.com'); // Default fallback
          }

          // Support phone - Always fetch fresh
          const supportPhoneKey = 'support_phone';
          try {
            const result = await dispatch(getSettingValue(supportPhoneKey)).unwrap();
            const value = result?.value || null;
            setSupportPhone(value || '+90 (XXX) XXX XX XX');
          } catch (error) {
            console.error('Error loading support_phone:', error);
            setSupportPhone('+90 (XXX) XXX XX XX'); // Default fallback
          }
        } catch (error) {
          console.error('Error loading payment settings:', error);
          // Default to false
          setPaymentGatewayEnabled(false);
        }
      };

      loadSettings();
      
      // Reset state or set preselected package
      if (preselectedPackage) {
        setSelectedPackage(preselectedPackage);
        setCurrentStep(2); // If package is preselected, go to payment step
      } else {
        setSelectedPackage(null);
        setCurrentStep(1); // Reset to first step
      }
      setLoading(false);
    }
  }, [open, dispatch, preselectedPackage]);

  const handlePackageSelect = (packageId) => {
    const pkg = activePackages.find(p => p._id === packageId);
    setSelectedPackage(pkg);
  };

  const handleNext = () => {
    if (selectedPackage && paymentGatewayEnabled) {
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  const handlePaymentSubmit = async (data) => {
    if (!selectedPackage) {
      alertify.error('Lütfen bir paket seçin');
      return;
    }

    setLoading(true);
    try {
      await dispatch(purchaseCredits({
        packageId: selectedPackage._id,
        paymentData: data,
        source: onSuccess ? 'wizard' : 'page' // Document wizard'dan açıldıysa 'wizard'
      })).unwrap();

      // Refresh balance
      await dispatch(fetchBalance());

      alertify.success('Kredi başarıyla yüklendi');
      
      // Call onSuccess callback
      if (onSuccess) {
        onSuccess();
      }

      // Close modal
      onClose();
      // Reset wizard state
      setCurrentStep(1);
      setSelectedPackage(null);
    } catch (error) {
      alertify.error(error.message || 'Ödeme başarısız');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Reset wizard state
      setCurrentStep(1);
      setSelectedPackage(null);
      onClose();
    }
  };

  const isLoading = creditLoading || packageLoading || loading;

  // Check if payment is enabled
  // For wizard flow, we need paymentGatewayEnabled (not document_wizard_payment_enabled check)
  const isPaymentEnabled = paymentGatewayEnabled;

  const steps = ['Paket Seçimi', 'Ödeme Bilgileri'];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Kredi Satın Al</Typography>
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{ minWidth: 'auto', p: 1 }}
          >
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Stepper */}
        <Box sx={{ mb: 4, mt: 2 }}>
          <Stepper activeStep={currentStep - 1} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Step Content */}
        <Box sx={{ position: 'relative', minHeight: 400 }}>
          {/* Step 1: Package Selection */}
          {currentStep === 1 && (
            <Fade in={currentStep === 1} timeout={300}>
              <Box>
                {/* Current Balance */}
                <Card sx={{ mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                  <CardContent sx={{ py: 2 }}>
                    <Typography variant="h6" align="center" sx={{ mb: requiredCredits ? 0.5 : 0 }}>
                      Mevcut Bakiye: {balance} Kredi
                    </Typography>
                    {requiredCredits && (
                      <Typography variant="body2" align="center" sx={{ fontSize: '0.875rem' }}>
                        Gerekli: {requiredCredits} Kredi
                        {balance < requiredCredits && (
                          <span style={{ color: '#ffeb3b', display: 'block', marginTop: '4px' }}>
                            (Eksik: {requiredCredits - balance} Kredi)
                          </span>
                        )}
                      </Typography>
                    )}
                  </CardContent>
                </Card>

                {/* Loading State */}
                {isLoading && activePackages.length === 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                )}

                {/* Payment Gateway Disabled */}
                {!isPaymentEnabled && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body1" gutterBottom>
                      <strong>Ödeme sistemi şu anda kapalı</strong>
                    </Typography>
                    <Typography variant="body2">
                      Kredi yüklemesi için lütfen sistem yetkilileri ile iletişime geçiniz.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>E-posta:</strong> {supportEmail}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Telefon:</strong> {supportPhone}
                    </Typography>
                  </Alert>
                )}

                {/* Package Selection */}
                {activePackages.length > 0 && (
                  <>
                    <Typography variant="h6" gutterBottom>
                      Kredi Paketleri
                    </Typography>
                    <RadioGroup
                      value={selectedPackage?._id || (preselectedPackage?._id) || ''}
                      onChange={(e) => handlePackageSelect(e.target.value)}
                    >
                      <Grid container spacing={2}>
                        {activePackages.map((pkg) => {
                          const totalCredits = pkg.credits + (pkg.bonusCredits || 0);
                          const pricePerCredit = pkg.price / totalCredits;
                          
                          return (
                            <Grid key={pkg._id} size={{ xs: 12, sm: 4 }}>
                              <Card
                                sx={{
                                  minHeight: 300,
                                  cursor: 'pointer',
                                  border: selectedPackage?._id === pkg._id ? 2 : 1,
                                  borderColor: selectedPackage?._id === pkg._id ? 'primary.main' : 'divider',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    borderColor: 'primary.main',
                                    boxShadow: 4,
                                    transform: 'translateY(-4px)'
                                  }
                                }}
                                onClick={() => handlePackageSelect(pkg._id)}
                              >
                                <CardContent>
                                  <FormControlLabel
                                    value={pkg._id}
                                    control={<Radio />}
                                    label={
                                      <Box>
                                        <Typography variant="h6" fontWeight="bold">{pkg.name}</Typography>
                                        <Typography variant="h4" color="primary" sx={{ my: 1 }}>
                                          {pkg.credits} Kredi
                                        </Typography>
                                        {pkg.bonusCredits > 0 && (
                                          <>
                                          <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                                            +{pkg.bonusCredits} Bonus Kredi
                                          </Typography>
                                          <Typography variant="body2" color="primary.main" sx={{ mb: 1 }}>
                                          Toplamda  {totalCredits} Kredi
                                        </Typography>
                                        </>
                                        )}
                                        <Typography variant="h5" sx={{ mt: 2, mb: 0.5 }}>
                                          {pkg.price.toFixed(2)} TL
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {pricePerCredit.toFixed(2)} TL / Kredi
                                        </Typography>
                                        {pkg.description && (
                                          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                            {pkg.description}
                                          </Typography>
                                        )}
                                      </Box>
                                    }
                                    sx={{ width: '100%', m: 0 }}
                                  />
                                </CardContent>
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </RadioGroup>
                  </>
                )}

                {/* No Packages Message */}
                {!isLoading && activePackages.length === 0 && (
                  <Alert severity="warning">
                    Şu anda aktif kredi paketi bulunmamaktadır.
                  </Alert>
                )}
              </Box>
            </Fade>
          )}

          {/* Step 2: Payment Form */}
          {currentStep === 2 && (
            <Slide direction="left" in={currentStep === 2} timeout={300}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {/* Selected Package Summary */}
                {selectedPackage && (
                  <Card sx={{ mb: 3, bgcolor: 'grey.50', border: 1, borderColor: 'primary.main', width: '100%', maxWidth: 600 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Seçilen Paket
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            {selectedPackage.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedPackage.credits + (selectedPackage.bonusCredits || 0)} Kredi
                            {selectedPackage.bonusCredits > 0 && (
                              <span style={{ color: '#4caf50' }}>
                                {' '}(+{selectedPackage.bonusCredits} Bonus)
                              </span>
                            )}
                          </Typography>
                        </Box>
                        <Typography variant="h5" color="primary" fontWeight="bold">
                          {selectedPackage.price.toFixed(2)} TL
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Payment Form */}
                {isPaymentEnabled && selectedPackage && (
                  <Box sx={{ width: '100%', maxWidth: 600 }}>
                    <PaymentForm
                      onSubmit={handlePaymentSubmit}
                      loading={loading}
                      disabled={!selectedPackage}
                    />
                  </Box>
                )}
              </Box>
            </Slide>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          color="inherit"
        >
          İptal
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        {currentStep === 1 && (
          <Button
            onClick={handleNext}
            variant="contained"
            color="primary"
            disabled={!selectedPackage || !isPaymentEnabled || loading}
            endIcon={<ArrowForwardIcon />}
          >
            İleri
          </Button>
        )}
        {currentStep === 2 && (
          <>
            <Button
              onClick={handleBack}
              disabled={loading}
              startIcon={<ArrowBackIcon />}
              sx={{ mr: 1 }}
            >
              Geri
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreditPurchaseModal;

